// Closing the day and the reports that closing leaves behind (slice 51b,
// ADR 0047; Addendum D7 and D26).
//
// The page draws what the server decided and asks the server to change it; it
// re-derives nothing. A day with something outstanding is closed only when the
// owner or manager says so, and that does not make it final: the stamp stays
// provisional until what is outstanding is dealt with. Every closed day keeps
// the exact report it had when it was closed.

import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE, TOKEN_STORAGE_KEY, apiFetch } from '../../api/client.js';
import type {
  ClosePeriodResponse,
  DayReport,
  StoredReport,
  StoredReportMeta,
} from '../../api/pos-types.js';
import { Chip, actionErrorText } from './common.js';
import { formatDateTime, formatRupees } from './format.js';
import { BLOCKER_TEXT, STATUS_TEXT } from './labels.js';

export interface PeriodPanelProps {
  readonly orgId: string;
  readonly outletId: string;
  readonly date: string;
  readonly report: DayReport;
  /** pos:period_close */
  readonly canClose: boolean;
  /** pos:period_reopen */
  readonly canReopen: boolean;
}

const STATE_TEXT = {
  open: 'Open',
  soft_closed: 'Reviewed, not closed',
  closed: 'Closed',
  reopened: 'Reopened',
} as const;

const REASON_TEXT = {
  period_closed: 'Saved when the day was closed',
  regenerated: 'Saved again later',
} as const;

export function PeriodPanel({
  orgId,
  outletId,
  date,
  report,
  canClose,
  canReopen,
}: PeriodPanelProps): JSX.Element {
  const qc = useQueryClient();
  const base = `/organizations/${orgId}/outlets/${outletId}/pos`;
  const [note, setNote] = useState('');
  const [anyway, setAnyway] = useState(false);
  const [reason, setReason] = useState('');
  const [shown, setShown] = useState<number | null>(null);

  const period = report.period;
  const blockers = report.stamp.blockers.filter((b) => b.code !== 'period_missing');
  const versionsKey = ['pos-report-versions', orgId, outletId, date] as const;

  const versions = useQuery({
    queryKey: versionsKey,
    queryFn: () =>
      apiFetch<{ versions: StoredReportMeta[] }>(
        `${base}/reports/day/versions?businessDate=${date}`,
      ),
  });

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['pos-day', orgId, outletId] });
    void qc.invalidateQueries({ queryKey: versionsKey });
  };

  const softClose = useMutation({
    mutationFn: () =>
      apiFetch<unknown>(`${base}/periods/${date}/soft-close`, { method: 'POST', body: {} }),
    onSettled: refresh,
  });
  const close = useMutation({
    mutationFn: () =>
      apiFetch<ClosePeriodResponse>(`${base}/periods/${date}/close`, {
        method: 'POST',
        body: {
          acknowledgeBlockers: anyway,
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      }),
    onSuccess: () => {
      setNote('');
      setAnyway(false);
    },
    onSettled: refresh,
  });
  const reopen = useMutation({
    mutationFn: () =>
      apiFetch<unknown>(`${base}/periods/${date}/reopen`, {
        method: 'POST',
        body: { reason: reason.trim() },
      }),
    onSuccess: () => setReason(''),
    onSettled: refresh,
  });
  const regenerate = useMutation({
    mutationFn: () =>
      apiFetch<unknown>(`${base}/reports/day/regenerate`, {
        method: 'POST',
        body: { businessDate: date },
      }),
    onSettled: refresh,
  });
  const busy = softClose.isPending || close.isPending || reopen.isPending || regenerate.isPending;
  const error = actionErrorText(close.error ?? softClose.error ?? reopen.error ?? regenerate.error);

  const stored = useQuery({
    queryKey: ['pos-report-stored', orgId, outletId, date, shown],
    enabled: shown !== null,
    queryFn: () =>
      apiFetch<StoredReport>(`${base}/reports/day/stored?businessDate=${date}&version=${shown}`),
  });

  const closed = period?.state === 'closed';
  const canCloseNow = canClose && period !== null && !closed;
  const canReviewNow = canClose && (period?.state === 'open' || period?.state === 'reopened');
  const canReopenNow = canReopen && (period?.state === 'closed' || period?.state === 'soft_closed');

  return (
    <section className="card period-panel" aria-labelledby="period-heading">
      <h3 id="period-heading">Closing this day</h3>
      {period ? (
        <p>
          <Chip tone={closed ? 'ok' : undefined}>{STATE_TEXT[period.state]}</Chip>
          {period.closedAt && closed ? ` since ${formatDateTime(period.closedAt)}` : ''}
          {period.reopenedAt && !closed ? ` · reopened ${formatDateTime(period.reopenedAt)}` : ''}
        </p>
      ) : (
        <p className="muted">
          Nothing has been received for this day, so there is nothing to close.
        </p>
      )}
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      {canCloseNow ? (
        <div className="period-close">
          {blockers.length > 0 ? (
            <>
              <p>Something for this day is still outstanding:</p>
              <ul className="stamp-blockers">
                {blockers.map((b) => (
                  <li key={b.code}>{BLOCKER_TEXT[b.code](b.count)}</li>
                ))}
              </ul>
              <label className="inline-field">
                <input
                  type="checkbox"
                  checked={anyway}
                  onChange={(e) => setAnyway(e.target.checked)}
                />{' '}
                Close it anyway. The day stays provisional until this is dealt with.
              </label>
            </>
          ) : (
            <p className="muted">
              Nothing is outstanding. Closing keeps this report as it stands and sends any later
              record for this day to the current day as a correction.
            </p>
          )}
          <label className="inline-field">
            Note (optional){' '}
            <input
              type="text"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <div className="row-actions">
            <button
              type="button"
              disabled={busy || (blockers.length > 0 && !anyway)}
              onClick={() => close.mutate()}
            >
              Close the day
            </button>
            {canReviewNow ? (
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => softClose.mutate()}
              >
                Mark as reviewed
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {!canClose && period && !closed ? (
        <p className="muted">Only an owner or manager can close a day.</p>
      ) : null}

      {canReopenNow ? (
        <div className="period-reopen">
          <label className="inline-field">
            Why is it being reopened?{' '}
            <input
              type="text"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <div className="row-actions">
            <button
              type="button"
              className="secondary"
              disabled={busy || reason.trim() === ''}
              onClick={() => reopen.mutate()}
            >
              Reopen the day
            </button>
          </div>
        </div>
      ) : null}
      {!canReopen && (closed || period?.state === 'soft_closed') ? (
        <p className="muted">Only an owner can reopen a closed day.</p>
      ) : null}

      <h4>Saved reports</h4>
      {versions.data && versions.data.versions.length === 0 ? (
        <p className="muted">
          No report has been saved for this day. One is saved each time the day is closed.
        </p>
      ) : null}
      <ul className="report-versions">
        {(versions.data?.versions ?? []).map((v) => (
          <li key={v.version}>
            <strong>Version {v.version}</strong>{' '}
            <Chip tone={v.reconciliationStatus === 'final' ? 'ok' : 'warn'}>
              {STATUS_TEXT[v.reconciliationStatus].label}
            </Chip>{' '}
            <span className="muted">
              {REASON_TEXT[v.reason]} · {formatDateTime(v.generatedAt)} · check{' '}
              <code>{v.reportSha256.slice(0, 12)}</code>
            </span>{' '}
            <button
              type="button"
              className="link-button"
              onClick={() => setShown(shown === v.version ? null : v.version)}
            >
              {shown === v.version ? 'Hide' : 'Show'}
            </button>{' '}
            <button
              type="button"
              className="link-button"
              onClick={() => void download(base, date, v.version)}
            >
              Download
            </button>
          </li>
        ))}
      </ul>
      {shown !== null && stored.data ? (
        <div className="card" data-testid="stored-report">
          <p>
            Version {stored.data.version} as it stood on {formatDateTime(stored.data.generatedAt)}:{' '}
            {formatRupees(stored.data.report.sales.original.totalPaise)} from{' '}
            {stored.data.report.sales.original.billCount} bills,{' '}
            {STATUS_TEXT[stored.data.report.stamp.reconciliationStatus].label.toLowerCase()}.
          </p>
          <p className="muted">
            This is the saved copy; it does not change if later records arrive.
          </p>
        </div>
      ) : null}
      {closed && canClose ? (
        <div className="row-actions">
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={() => regenerate.mutate()}
          >
            Save a fresh report
          </button>
          <span className="muted">Saves how the day reads now as the next version.</span>
        </div>
      ) : null}
    </section>
  );
}

/** Saves the exact stored text, which is what the check digits were computed over. */
async function download(base: string, date: string, version: number): Promise<void> {
  let token: string | null = null;
  try {
    token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    /* no token: the request will 401 and the session ends the normal way */
  }
  const res = await fetch(
    `${API_BASE}${base}/reports/day/stored/raw?businessDate=${date}&version=${version}`,
    token ? { headers: { authorization: `Bearer ${token}` } } : {},
  );
  if (!res.ok) return;
  const url = URL.createObjectURL(new Blob([await res.text()], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `day-report-${date}-v${version}.json`;
  a.click();
  URL.revokeObjectURL(url);
}
